class SingleGallery {
	constructor(selector = ".single-gallery-page") {
		this.gallery = document.querySelector(selector)
		this.items = []
		this.currentIndex = 0
		this.lightbox = null
		this.image = null
		this.counter = null
		this.copyButton = null
		this.shareStatus = null

		this.init()
	}

	init() {
		if (!this.gallery) {
			return
		}

		this.items = Array.from(this.gallery.querySelectorAll("[data-gallery-preview-item]"))
			.map(item => {
				const image = item.querySelector("img")

				if (!image) {
					return null
				}

				return {
					trigger: item,
					src: item.dataset.gallerySrc || image.currentSrc || image.src,
					alt: image.alt || ""
				}
			})
			.filter(Boolean)

		if (!this.items.length) {
			return
		}

		this.createLightbox()
		this.bindEvents()
		this.bindShareEvents()
	}

	createLightbox() {
		this.lightbox = document.createElement("div")
		this.lightbox.className = "gallery-lightbox"
		this.lightbox.setAttribute("aria-hidden", "true")
		this.lightbox.setAttribute("role", "dialog")
		this.lightbox.setAttribute("aria-label", "Gallery image preview")

		this.lightbox.innerHTML = `
			<button class="gallery-lightbox__close" type="button" aria-label="Close image preview">
				<i class="fa-solid fa-xmark" aria-hidden="true"></i>
			</button>

			<button class="gallery-lightbox__nav gallery-lightbox__nav--prev" type="button" aria-label="Previous image">
				<i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
			</button>

			<figure class="gallery-lightbox__figure">
				<img class="gallery-lightbox__image" src="" alt="">
				<figcaption class="gallery-lightbox__counter"></figcaption>
			</figure>

			<button class="gallery-lightbox__nav gallery-lightbox__nav--next" type="button" aria-label="Next image">
				<i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
			</button>
		`

		document.body.appendChild(this.lightbox)

		this.image = this.lightbox.querySelector(".gallery-lightbox__image")
		this.counter = this.lightbox.querySelector(".gallery-lightbox__counter")
	}

	bindEvents() {
		this.items.forEach((item, index) => {
			item.trigger.addEventListener("click", () => this.open(index))
			item.trigger.addEventListener("keydown", event => {
				if (event.key !== "Enter" && event.key !== " ") {
					return
				}

				event.preventDefault()
				this.open(index)
			})
		})

		this.lightbox.querySelector(".gallery-lightbox__close").addEventListener("click", () => this.close())
		this.lightbox.querySelector(".gallery-lightbox__nav--prev").addEventListener("click", () => this.showPrevious())
		this.lightbox.querySelector(".gallery-lightbox__nav--next").addEventListener("click", () => this.showNext())

		this.lightbox.addEventListener("click", event => {
			if (event.target === this.lightbox) {
				this.close()
			}
		})

		document.addEventListener("keydown", event => this.handleKeydown(event))
	}

	bindShareEvents() {
		this.copyButton = this.gallery.querySelector("[data-gallery-copy-link]")
		this.shareStatus = this.gallery.querySelector("[data-gallery-share-status]")

		if (!this.copyButton) {
			return
		}

		this.copyButton.addEventListener("click", () => this.copyShareLink())
	}

	async copyShareLink() {
		const link = this.copyButton.dataset.galleryCopyLink

		if (!link) {
			return
		}

		try {
			if (navigator.clipboard) {
				await navigator.clipboard.writeText(link)
			} else {
				this.copyWithFallback(link)
			}

			this.showShareStatus("Link copied for Instagram")
		} catch (error) {
			const didCopy = this.copyWithFallback(link)
			this.showShareStatus(didCopy ? "Link copied for Instagram" : "Copy this page URL to share on Instagram")
		}
	}

	copyWithFallback(link) {
		const field = document.createElement("textarea")

		field.value = link
		field.setAttribute("readonly", "")
		field.style.position = "fixed"
		field.style.top = "-999px"
		field.style.left = "-999px"

		document.body.appendChild(field)
		field.select()

		let didCopy = false

		try {
			didCopy = document.execCommand("copy")
		} catch (error) {
			didCopy = false
		}

		field.remove()

		return didCopy
	}

	showShareStatus(message) {
		if (!this.shareStatus) {
			return
		}

		this.shareStatus.textContent = message

		window.clearTimeout(this.shareStatusTimer)
		this.shareStatusTimer = window.setTimeout(() => {
			this.shareStatus.textContent = ""
		}, 3000)
	}

	open(index) {
		this.currentIndex = index
		this.update()
		this.lightbox.classList.add("is-active")
		this.lightbox.setAttribute("aria-hidden", "false")
		document.body.classList.add("body-no-scroll")
		this.lightbox.querySelector(".gallery-lightbox__close").focus()
	}

	close() {
		this.lightbox.classList.remove("is-active")
		this.lightbox.setAttribute("aria-hidden", "true")
		document.body.classList.remove("body-no-scroll")
		this.items[this.currentIndex]?.trigger.focus()
	}

	showPrevious() {
		this.currentIndex = (this.currentIndex - 1 + this.items.length) % this.items.length
		this.update()
	}

	showNext() {
		this.currentIndex = (this.currentIndex + 1) % this.items.length
		this.update()
	}

	update() {
		const item = this.items[this.currentIndex]

		this.image.src = item.src
		this.image.alt = item.alt
		this.counter.textContent = `${this.currentIndex + 1} / ${this.items.length}`
	}

	handleKeydown(event) {
		if (!this.lightbox?.classList.contains("is-active")) {
			return
		}

		if (event.key === "Escape") {
			this.close()
		}

		if (event.key === "ArrowLeft") {
			this.showPrevious()
		}

		if (event.key === "ArrowRight") {
			this.showNext()
		}
	}
}

export default SingleGallery
